import { useId, forwardRef, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import { IconChevronDown } from './Icon.js';

/**
 * Form fields.
 *
 * ── THE PROBLEM THIS SOLVES ───────────────────────────────────────────────
 *
 * A label, a control, a hint and an error message have to be wired together
 * with `id`, `htmlFor`, `aria-describedby` and `aria-invalid` for a screen
 * reader to announce "Amount, edit text, in paise, invalid: must be a number".
 * Nobody does that by hand every time. In v1 they were plain `<label><input>`
 * pairs, so none of it was announced.
 *
 * `<Field>` generates the ids and distributes them. A caller writes the four
 * pieces and the wiring is not theirs to get wrong.
 *
 * ── ONE HEIGHT SCALE ──────────────────────────────────────────────────────
 *
 * Input, select and button all resolve height from `--control-height-*`. They
 * sit on the same toolbar row constantly, and nothing looks more careless than
 * a search box one pixel taller than the button beside it.
 */

export type ControlSize = 'sm' | 'md' | 'lg';

const CONTROL_SIZE: Record<ControlSize, string> = {
  sm: 'h-(--control-height-sm) px-(--control-padding-x-sm) text-sm',
  md: 'h-(--control-height-md) px-(--control-padding-x-md) text-md',
  lg: 'h-(--control-height-lg) px-(--control-padding-x-lg) text-lg',
};

/** Shared by input, select and textarea so they cannot drift apart. */
const CONTROL_BASE = cn(
  'w-full rounded-(--control-radius)',
  'bg-raised text-primary border border-line',
  'transition-[border-color,box-shadow] duration-(--m-fast)',
  'hover:border-strong',
  'focus-visible:outline-none focus-visible:border-focus focus-visible:shadow-(--focus-ring)',
  'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-disabled disabled:hover:border-line',
  // `aria-invalid` drives the error styling rather than a separate `error` prop,
  // so the visual state and what a screen reader announces cannot disagree.
  'aria-[invalid=true]:border-danger-fg aria-[invalid=true]:hover:border-danger-fg',
);

/* ────────────────────────────────  Field  ──────────────────────────────── */

export interface FieldProps {
  label: ReactNode;
  /** Guidance shown under the control. Hidden while an error is showing. */
  hint?: ReactNode;
  /** When set, the field renders as invalid and this replaces the hint. */
  error?: ReactNode;
  /** Marks the field required, visually and to assistive tech. */
  required?: boolean;
  /** Hides the label visually but keeps it for screen readers. */
  labelHidden?: boolean;
  className?: string;
  /** Receives the wiring. Spread it onto the control. */
  children: (field: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean | undefined;
  }) => ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  labelHidden = false,
  className,
  children,
}: FieldProps) {
  const controlId = useId();
  const messageId = `${controlId}-message`;
  const invalid = Boolean(error);
  // Only one message is ever shown, so only one id is ever referenced. Pointing
  // `aria-describedby` at an element that is not rendered makes some screen
  // readers announce nothing at all.
  const message = error ?? hint;
  const describedBy = message ? messageId : undefined;

  return (
    <div className={cn('flex min-w-0 flex-col gap-(--field-gap)', className)}>
      <label
        htmlFor={controlId}
        className={cn(
          'text-(length:--field-label-size) font-semibold uppercase tracking-caps text-tertiary',
          // Kept in the accessibility tree, removed from view.
          labelHidden && 'sr-only',
        )}
      >
        {label}
        {required && (
          <span className="text-danger-fg ml-0.5" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {children({
        id: controlId,
        'aria-describedby': describedBy,
        'aria-invalid': invalid || undefined,
        required: required || undefined,
      })}

      {message && (
        <p
          id={messageId}
          // Errors are announced when they appear; hints are read only when the
          // control is focused. `alert` on a hint would interrupt constantly.
          role={invalid ? 'alert' : undefined}
          className={cn(
            'text-(length:--field-hint-size) leading-snug',
            invalid ? 'text-danger-fg font-medium' : 'text-tertiary',
          )}
        >
          {message}
        </p>
      )}
    </div>
  );
}

/* ────────────────────────────────  Input  ──────────────────────────────── */

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: ControlSize;
  /** An icon inside the field, at the start. Padding adjusts to clear it. */
  leading?: ReactNode;
  trailing?: ReactNode;
  /**
   * Right-aligns and switches on tabular figures. Use for every amount field —
   * a column of right-aligned amounts is how an accountant checks a total at a
   * glance, and a left-aligned one destroys that.
   */
  numeric?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { size = 'md', leading, trailing, numeric = false, className, ...rest },
  ref,
) {
  const control = (
    <input
      ref={ref}
      className={cn(
        CONTROL_BASE,
        CONTROL_SIZE[size],
        numeric && 'text-right tnum',
        leading && 'pl-8',
        trailing && 'pr-8',
        className,
      )}
      {...rest}
    />
  );

  if (!leading && !trailing) return control;

  return (
    <div className="relative w-full">
      {leading && (
        <span className="pointer-events-none absolute inset-y-0 left-0 grid w-8 place-items-center text-tertiary">
          {leading}
        </span>
      )}
      {control}
      {trailing && (
        <span className="pointer-events-none absolute inset-y-0 right-0 grid w-8 place-items-center text-tertiary">
          {trailing}
        </span>
      )}
    </div>
  );
});

/* ───────────────────────────────  Select  ─────────────────────────────── */

/**
 * A native <select>.
 *
 * Deliberately native rather than the Radix listbox. On a form with a hundred
 * account codes, the native control gives type-ahead, OS-level scrolling and
 * correct behaviour on mobile for free, and it never traps focus. Reach for the
 * Radix version only when options need rich content (icons, two-line entries),
 * which is a different component, not a bigger version of this one.
 */
export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  size?: ControlSize;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { size = 'md', className, children, ...rest },
  ref,
) {
  return (
    <div className="relative w-full">
      <select
        ref={ref}
        className={cn(
          CONTROL_BASE,
          CONTROL_SIZE[size],
          // Room for the chevron, and the native arrow removed so there is
          // only one.
          'appearance-none pr-8 cursor-pointer',
          className,
        )}
        {...rest}
      >
        {children}
      </select>
      <IconChevronDown
        size={14}
        className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-tertiary"
      />
    </div>
  );
});

/* ──────────────────────────────  Textarea  ─────────────────────────────── */

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, rows = 3, ...rest }, ref) {
    return (
      <textarea
        ref={ref}
        rows={rows}
        className={cn(
          CONTROL_BASE,
          'px-(--control-padding-x-md) py-2 text-md leading-normal resize-y min-h-16',
          className,
        )}
        {...rest}
      />
    );
  },
);

/* ────────────────────────────────  Row  ────────────────────────────────── */

